import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type {
  UnifiedError,
  ErrorFilters,
  ErrorSummary,
  ErrorDistribution,
  CompilerPayload,
  CompilerValidationError,
  ErrorSeverity,
} from "@/lib/error-types";
import type { Json } from "@/integrations/supabase/types";

interface UseCompilerErrorsResult {
  errors: UnifiedError[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  summary: ErrorSummary | null;
  distribution: ErrorDistribution[];
  summaryLoading: boolean;
  distributionLoading: boolean;
  fetchErrors: (filters?: ErrorFilters, page?: number) => Promise<void>;
  fetchSummary: () => Promise<void>;
  fetchDistribution: (startDate?: Date, endDate?: Date) => Promise<void>;
}

// Parse compilation_result payload to extract validation errors/warnings
function parseCompilerPayload(payload: Json): CompilerValidationError[] {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return [];
  }

  const p = payload as CompilerPayload;
  const results: CompilerValidationError[] = [];

  // Extract errors
  if (p.validation?.errors && Array.isArray(p.validation.errors)) {
    for (const err of p.validation.errors) {
      if (err && typeof err === 'object') {
        results.push({
          code: (err as { code?: string }).code || 'UNKNOWN',
          message: (err as { message?: string }).message || 'Unknown error',
          section_token: (err as { section_token?: string }).section_token,
          structure_key: (err as { structure_key?: string }).structure_key,
          severity: 'error',
        });
      }
    }
  }

  // Extract warnings
  if (p.validation?.warnings && Array.isArray(p.validation.warnings)) {
    for (const warn of p.validation.warnings) {
      if (warn && typeof warn === 'object') {
        results.push({
          code: (warn as { code?: string }).code || 'UNKNOWN',
          message: (warn as { message?: string }).message || 'Unknown warning',
          section_token: (warn as { section_token?: string }).section_token,
          structure_key: (warn as { structure_key?: string }).structure_key,
          severity: 'warning',
        });
      }
    }
  }

  return results;
}

/**
 * Hook for fetching compiler errors from compilation_result table
 */
export function useCompilerErrors(): UseCompilerErrorsResult {
  const [errors, setErrors] = useState<UnifiedError[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<ErrorSummary | null>(null);
  const [distribution, setDistribution] = useState<ErrorDistribution[]>([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [distributionLoading, setDistributionLoading] = useState(false);

  const fetchErrors = useCallback(async (filters?: ErrorFilters, pageNum: number = 1) => {
    setLoading(true);
    try {
      // Build query for compilation_result with reporting_run join
      let query = supabase
        .from('compilation_result')
        .select(`
          id,
          payload,
          error_count,
          warning_count,
          created_at,
          reporting_run_id,
          reporting_run:reporting_run_id (
            user_id,
            exam_family_id
          )
        `, { count: 'exact' })
        .order('created_at', { ascending: false });

      // Filter based on severity
      if (filters?.severity === 'error') {
        query = query.gt('error_count', 0);
      } else if (filters?.severity === 'warning') {
        query = query.gt('warning_count', 0);
      } else {
        // All: show rows with either errors or warnings
        query = query.or('error_count.gt.0,warning_count.gt.0');
      }

      // Date filters
      if (filters?.startDate) {
        query = query.gte('created_at', filters.startDate.toISOString());
      }
      if (filters?.endDate) {
        query = query.lte('created_at', filters.endDate.toISOString());
      }

      // Pagination
      query = query.range((pageNum - 1) * pageSize, pageNum * pageSize - 1);

      const { data, error, count } = await query;

      if (error) {
        // Handle missing table gracefully
        if (error.code === '42P01' || error.message?.includes('does not exist')) {
          console.warn('compilation_result table not found');
          setErrors([]);
          setTotal(0);
          setPage(pageNum);
          return;
        }
        throw error;
      }

      // Transform to UnifiedError format
      const unifiedErrors: UnifiedError[] = [];

      for (const row of data || []) {
        const validationErrors = parseCompilerPayload(row.payload);
        const reportingRun = row.reporting_run as { user_id: string; exam_family_id: string } | null;

        // Filter by severity if specified
        const filteredValidationErrors = filters?.severity
          ? validationErrors.filter(ve => ve.severity === filters.severity)
          : validationErrors;

        // Filter by code if specified
        const codeFilteredErrors = filters?.code
          ? filteredValidationErrors.filter(ve => ve.code === filters.code)
          : filteredValidationErrors;

        for (const ve of codeFilteredErrors) {
          unifiedErrors.push({
            id: `${row.id}-${ve.code}-${ve.section_token || 'unknown'}`,
            source: 'compiler',
            severity: ve.severity as ErrorSeverity,
            code: ve.code,
            message: ve.message,
            created_at: row.created_at,
            user_id: reportingRun?.user_id || null,
            user_email: null, // Would need separate query to profiles
            context: {
              reporting_run_id: row.reporting_run_id,
              exam_family_id: reportingRun?.exam_family_id,
              section_token: ve.section_token,
              structure_key: ve.structure_key,
            },
            raw_payload: row.payload,
          });
        }
      }

      setErrors(unifiedErrors);
      setTotal(count || 0);
      setPage(pageNum);
    } catch (err) {
      console.error('Failed to fetch compiler errors:', err);
      toast.error('Failed to load compiler errors');
      setErrors([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const now = new Date();
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      // Get error/warning counts for 24h
      const { count: count24h } = await supabase
        .from('compilation_result')
        .select('*', { count: 'exact', head: true })
        .or('error_count.gt.0,warning_count.gt.0')
        .gte('created_at', yesterday.toISOString());

      // Get total compilations for 24h
      const { count: total24h } = await supabase
        .from('compilation_result')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', yesterday.toISOString());

      // Get error/warning counts for 7d
      const { count: count7d } = await supabase
        .from('compilation_result')
        .select('*', { count: 'exact', head: true })
        .or('error_count.gt.0,warning_count.gt.0')
        .gte('created_at', lastWeek.toISOString());

      // Get total compilations for 7d
      const { count: total7d } = await supabase
        .from('compilation_result')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', lastWeek.toISOString());

      // Get recent results to find most common code
      const { data: recentResults } = await supabase
        .from('compilation_result')
        .select('payload')
        .or('error_count.gt.0,warning_count.gt.0')
        .gte('created_at', lastWeek.toISOString())
        .limit(100);

      // Count error codes
      const codeCounts: Record<string, number> = {};
      for (const row of recentResults || []) {
        const validationErrors = parseCompilerPayload(row.payload);
        for (const ve of validationErrors) {
          codeCounts[ve.code] = (codeCounts[ve.code] || 0) + 1;
        }
      }

      let mostCommonCode: string | null = null;
      let mostCommonCount = 0;
      for (const [code, count] of Object.entries(codeCounts)) {
        if (count > mostCommonCount) {
          mostCommonCode = code;
          mostCommonCount = count;
        }
      }

      setSummary({
        total_24h: count24h || 0,
        total_7d: count7d || 0,
        error_rate_24h: total24h ? ((count24h || 0) / total24h) * 100 : 0,
        error_rate_7d: total7d ? ((count7d || 0) / total7d) * 100 : 0,
        most_common_code: mostCommonCode,
        most_common_count: mostCommonCount,
      });
    } catch (err) {
      console.error('Failed to fetch compiler summary:', err);
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const fetchDistribution = useCallback(async (startDate?: Date, endDate?: Date) => {
    setDistributionLoading(true);
    try {
      let query = supabase
        .from('compilation_result')
        .select('payload')
        .or('error_count.gt.0,warning_count.gt.0');

      if (startDate) {
        query = query.gte('created_at', startDate.toISOString());
      }
      if (endDate) {
        query = query.lte('created_at', endDate.toISOString());
      }

      const { data } = await query.limit(500);

      // Count error codes
      const codeCounts: Record<string, number> = {};
      let totalCount = 0;

      for (const row of data || []) {
        const validationErrors = parseCompilerPayload(row.payload);
        for (const ve of validationErrors) {
          codeCounts[ve.code] = (codeCounts[ve.code] || 0) + 1;
          totalCount++;
        }
      }

      // Convert to distribution format
      const dist: ErrorDistribution[] = Object.entries(codeCounts)
        .map(([code, count]) => ({
          code,
          count,
          percentage: totalCount > 0 ? (count / totalCount) * 100 : 0,
        }))
        .sort((a, b) => b.count - a.count);

      setDistribution(dist);
    } catch (err) {
      console.error('Failed to fetch compiler distribution:', err);
      setDistribution([]);
    } finally {
      setDistributionLoading(false);
    }
  }, []);

  return {
    errors,
    total,
    page,
    pageSize,
    loading,
    summary,
    distribution,
    summaryLoading,
    distributionLoading,
    fetchErrors,
    fetchSummary,
    fetchDistribution,
  };
}
